import * as React from "react";
import { aiAuditEvents } from "~/AIEvents/aiAuditEvents";
import { useTrackEvent } from "~/common/audit";
import { useMessageContext } from "~/messageBoard/messageContext";
import { PostMessageViewModel } from "~/messageBoard/types";
import { messageBoardResx } from "~/resxDist/messageBoardResx";
import { ButtonDefault, ButtonProps, ButtonsGroup } from "~/sharedComponents/buttons";
import { useReduxAction } from "~/sharedComponents/hooks/useReduxAction";
import { icon } from "~/sharedComponents/icons";
import { useApplySuggestions } from "../hooks/useApplySuggestions";
import { retryAiCompletion } from "../state/sagas";

/**
 * This footer is shown in new message editor whenever
 * AI completion preview is visible in editor.
 */
export const AiCompletionFooter: React.FC = () => {
    const track = useTrackEvent();
    const vm = useMessageContext<PostMessageViewModel>();
    const focusEditor = () => vm.isFocused(true);

    const [isRetrying, setIsRetrying] = React.useState(false);
    const retryCompletion = useReduxAction(retryAiCompletion);
    const { append, replace, discard } = useApplySuggestions(vm);

    const retry = () => {
        setIsRetrying(true);

        track({
            eventName: aiAuditEvents.aiMessageRetried,
        });

        retryCompletion({
            callback: () => {
                setIsRetrying(false);
                focusEditor();
            },
        });
    };

    return (
        <ButtonsGroup maxWidth={"100%"}>
            <Button iconName={icon.check} disabled={isRetrying} text={messageBoardResx.Replace} action={replace} />
            <Button iconName={icon.plus} disabled={isRetrying} text={messageBoardResx.Append} action={append} />
            <Button iconName={icon.repeat} text={messageBoardResx.Retry} action={retry} isLoading={isRetrying} />
            <Button iconName={icon.eraser} disabled={isRetrying} text={messageBoardResx.Discard} action={discard} />
        </ButtonsGroup>
    );
};

const Button: React.FC<ButtonProps> = props => <ButtonDefault title={props.text} {...props} />;
